import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnInit,
  resource,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { email, form, required, submit } from '@angular/forms/signals';
import { firstValueFrom } from 'rxjs';
import { EventqButton, EventqInput } from '@ecam/eventq-components';
import {
  landingUrlForLog,
  logOktaDebug,
  serializeUnknownError,
} from '../../../../core/auth/okta-debug-log';
import { AgentDataService } from '../../../../core/services/agent-data.service';
import { AgentAccessService } from '../../../../core/services/agent-access.service';
import { AuthSessionService } from '../../../../core/services/auth-session.service';
import { OktaAuthService } from '../../../../core/services/okta-auth.service';
import { TAB_EVENTS_PAGE, TAB_WAITING_PAGE } from '../../../../core/routing/browser-tab-routes';

@Component({
  selector: 'app-login',
  imports: [EventqInput, EventqButton],
  templateUrl: './login.html',
  styleUrl: './login.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Login implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly auth = inject(AuthSessionService);
  private readonly access = inject(AgentAccessService);
  private readonly agentData = inject(AgentDataService);
  private readonly okta = inject(OktaAuthService);
  private readonly destroyRef = inject(DestroyRef);
  private defaultEmailApplied = false;

  readonly loginModel = signal({
    email: '',
    password: '',
  });

  readonly loginForm = form(this.loginModel, (schemaPath) => {
    required(schemaPath.email, { message: 'Email is required' });
    email(schemaPath.email, { message: 'Enter a valid email' });
    required(schemaPath.password, { message: 'Password is required' });
  });

  /** Off while Okta is the only sign-in path. Flip to show email/password. */
  readonly credentialLoginEnabled = signal(false);

  readonly submitAttempted = signal(false);
  readonly submitting = signal(false);
  readonly loginError = signal<string | null>(null);
  readonly sessionDefaultsError = signal<string | null>(null);
  readonly oktaUnavailableMessage = signal<string | null>(null);

  /** Back-compat accessors used by existing specs and bindings. */
  readonly email = computed(() => this.loginForm.email().value());
  readonly password = computed(() => this.loginForm.password().value());

  readonly emailError = computed(() => {
    if (!this.submitAttempted() && !this.loginForm.email().touched()) {
      return null;
    }
    const errors = this.loginForm.email().errors();
    return errors[0]?.message ?? null;
  });

  readonly passwordError = computed(() => {
    if (!this.submitAttempted() && !this.loginForm.password().touched()) {
      return null;
    }
    const errors = this.loginForm.password().errors();
    return errors[0]?.message ?? null;
  });

  readonly sessionDefaults = resource({
    params: () => (this.credentialLoginEnabled() ? true : undefined),
    loader: async () => {
      try {
        this.sessionDefaultsError.set(null);
        return await firstValueFrom(this.agentData.getLoginSessionDefaults());
      } catch (error: unknown) {
        this.sessionDefaultsError.set('Could not load session defaults.');
        throw error;
      }
    },
  });

  constructor() {
    effect(() => {
      const defaults = this.sessionDefaults.value();
      if (!defaults || this.defaultEmailApplied) {
        return;
      }
      this.defaultEmailApplied = true;
      if (!this.loginForm.email().value()) {
        this.loginModel.update((model) => ({ ...model, email: defaults.defaultEmail }));
      }
    });

    const onPageShow = (): void => {
      this.redirectIfAuthenticated();
    };
    window.addEventListener('pageshow', onPageShow);
    this.destroyRef.onDestroy(() => window.removeEventListener('pageshow', onPageShow));
  }

  ngOnInit(): void {
    logOktaDebug(
      'Login.ngOnInit — EventQ /login (callback would log LoginOktaCallback.ngOnInit)',
      landingUrlForLog(),
    );
    if (this.redirectIfAuthenticated()) {
      return;
    }
    this.surfaceOktaQueryError();
  }

  /** Back from Okta often restores `/login` from bfcache while the session is still live. */
  private redirectIfAuthenticated(): boolean {
    if (!this.auth.isAuthenticated()) {
      return false;
    }
    const landing = this.access.canOpenEventsDashboard() ? TAB_EVENTS_PAGE : TAB_WAITING_PAGE;
    void this.router.navigate(['/t1', landing], { replaceUrl: true });
    return true;
  }

  setEmail(value: string): void {
    this.loginForm.email().value.set(value);
  }

  setPassword(value: string): void {
    this.loginForm.password().value.set(value);
  }

  onLogin(): void {
    void this.authenticate();
  }

  onOktaLogin(): void {
    this.oktaUnavailableMessage.set(null);
    logOktaDebug('Login.onOktaLogin: click');

    if (!this.okta.isConfigured()) {
      logOktaDebug('Login.onOktaLogin: Okta not configured — showing message');
      this.oktaUnavailableMessage.set(
        'Okta is not configured. Set issuer and clientId in environment.',
      );
      return;
    }

    logOktaDebug('Login.onOktaLogin: starting redirect');
    void this.okta.loginWithRedirect().catch((error: unknown) => {
      console.error('[Okta] failed to start redirect', error);
      logOktaDebug('Login.onOktaLogin: redirect failed', {
        error: serializeUnknownError(error),
      });
      this.oktaUnavailableMessage.set('Could not start Okta login. Try again.');
    });
  }

  onSubmit(event: Event): void {
    event.preventDefault();
    void this.authenticate();
  }

  /**
   * Okta denied/policy failures often land on /login with ?error=… instead of
   * /login/callback — surface them so console + UI show the real reason.
   */
  private surfaceOktaQueryError(): void {
    const params = this.route.snapshot.queryParamMap;
    const error = params.get('error');
    const description = params.get('error_description');
    if (!error && !description) {
      return;
    }

    console.error('[Okta] redirected to /login with error', { error, description });
    logOktaDebug('Login.surfaceOktaQueryError: Okta returned error query params', {
      error,
      description,
      queryKeys: [...params.keys],
    });

    this.oktaUnavailableMessage.set(
      description ?? error ?? 'Okta login failed. Check policy configuration.',
    );

    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      replaceUrl: true,
    });
  }

  private async authenticate(): Promise<void> {
    this.submitAttempted.set(true);
    this.loginError.set(null);
    this.oktaUnavailableMessage.set(null);

    if (this.submitting()) {
      return;
    }

    await submit(this.loginForm, async () => {
      this.submitting.set(true);
      try {
        const session = await firstValueFrom(
          this.agentData.login(this.loginForm.email().value(), this.loginForm.password().value()),
        );
        this.auth.establishSession(session.accessToken, session.email, {
          role: session.role,
          displayName: session.displayName,
        });
        const landing = this.access.canOpenEventsDashboard() ? TAB_EVENTS_PAGE : TAB_WAITING_PAGE;
        await this.router.navigate(['/t1', landing], { replaceUrl: true });
      } catch {
        this.loginError.set('Login failed. Check your email and password, then try again.');
      } finally {
        this.submitting.set(false);
      }
    });
  }
}
